import {
  FileText, KeyRound, LayoutDashboard, LogOut, Menu, Moon, Scissors, Settings, Sun, X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../auth';

function useTheme() {
  const [theme, setTheme] = useState<'light' | 'dark'>(
    () => (localStorage.getItem('snipwork-clients.theme') as 'light' | 'dark') ?? 'light',
  );
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('snipwork-clients.theme', theme);
  }, [theme]);
  return [theme, () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))] as const;
}

export default function Layout() {
  const { user, signOut } = useAuth();
  const [theme, toggleTheme] = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);

  const links = [
    { to: '/', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { to: '/documents', label: 'Documents', icon: FileText },
    ...(user?.role === 'owner' ? [{ to: '/admin', label: 'Admin', icon: Settings }] : []),
    { to: '/account', label: 'Account', icon: KeyRound },
  ];

  return (
    <div className="min-h-screen">
      <header className="no-print bg-ink-900 text-paper-100 shadow-sm">
        <div className="mx-auto flex max-w-[1400px] items-center gap-4 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="rounded-lg bg-brand-500 p-1.5 text-white">
              <Scissors className="h-4 w-4" aria-hidden />
            </span>
            <span className="text-sm font-semibold leading-tight">Snip.work App</span>
          </div>

          <nav className="ml-4 hidden flex-1 items-center gap-1 lg:flex" aria-label="Main">
            {links.map(({ to, label, icon: Icon, exact }) => (
              <NavLink
                key={to}
                to={to}
                end={exact}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? 'bg-brand-500 text-white' : 'text-paper-300 hover:bg-white/10 hover:text-paper-50'
                  }`
                }
              >
                <Icon className="h-4 w-4" aria-hidden />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <span className="hidden text-sm text-paper-400 xl:inline">{user?.email}</span>
            <button
              type="button"
              onClick={toggleTheme}
              className="rounded-lg p-2 text-paper-300 hover:bg-white/10 hover:text-paper-50"
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" aria-hidden /> : <Moon className="h-4 w-4" aria-hidden />}
            </button>
            <button
              type="button"
              onClick={signOut}
              className="flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm font-medium
                         text-white hover:bg-white/20"
            >
              <LogOut className="h-4 w-4" aria-hidden />
              <span className="hidden sm:inline">Sign out</span>
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="rounded-lg p-2 text-paper-300 hover:bg-white/10 lg:hidden"
              aria-label="Menu"
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav className="border-t border-white/10 px-4 pb-3 lg:hidden" aria-label="Main (mobile)">
            {links.map(({ to, label, icon: Icon, exact }) => (
              <NavLink
                key={to}
                to={to}
                end={exact}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `mt-1 flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium ${
                    isActive ? 'bg-brand-500 text-white' : 'text-paper-300 hover:bg-white/10'
                  }`
                }
              >
                <Icon className="h-4 w-4" aria-hidden />
                {label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
